/* eslint-disable react-dom/no-dangerously-set-innerhtml */
import * as React from 'react';
import DOMPurify from 'dompurify';
import { MoreHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAttachmentsToken } from '@/hooks/use-attachments-token';
import { splitEmailContentByBlockquote } from '@/lib/email';
import { getCidReferencesFromHtml, rewriteCidImagesToNylasAttachments } from '@/lib/nylas-inline-images';
import type { CommunicationDisplay } from '@/pages/app/deals/communications/types/communications';
import type { Message as NylasMessage } from 'nylas';

type MessageContentProps = {
  content: Partial<NylasMessage> | null;
  attachments?: CommunicationDisplay['attachments'];
  emailGrantId?: string;
  emailMessageId?: string;
  enableInlineImages?: boolean;
};

export function MessageContent({
  content,
  attachments,
  emailGrantId,
  emailMessageId,
  enableInlineImages,
}: MessageContentProps) {
  const [showQuotedContent, setShowQuotedContent] = React.useState(false);

  const { token } = useAttachmentsToken(!!enableInlineImages);

  const rewritten = React.useMemo(() => {
    if (!enableInlineImages) {
      return {
        html: content?.body ?? '',
        cidImageCount: 0,
        replacedCount: 0,
      };
    }

    return rewriteCidImagesToNylasAttachments({
      html: content?.body ?? '',
      attachments,
      grantId: emailGrantId,
      messageId: emailMessageId,
      token,
    });
  }, [attachments, content?.body, emailGrantId, emailMessageId, enableInlineImages, token]);

  // DEV-only diagnostics to understand why cid rewriting might not happen.
  React.useEffect(() => {
    if (!enableInlineImages)
      return;

    if (!import.meta.env.DEV)
      return;

    const body = content?.body ?? '';
    const cidRefs = getCidReferencesFromHtml(body);
    const contentIds = (attachments ?? []).map((a) => a.contentId).filter(Boolean);

    console.debug('[inline-images]', {
      grantId: emailGrantId,
      messageId: emailMessageId,
      hasToken: Boolean(token),
      attachments: attachments?.length ?? 0,
      attachmentContentIdCount: contentIds.length,
      cidRefs,
      contentIds,
      cidImageCount: rewritten.cidImageCount,
      replacedCount: rewritten.replacedCount,
    });
  }, [attachments, content?.body, emailGrantId, emailMessageId, enableInlineImages, rewritten.cidImageCount, rewritten.replacedCount, token]);

  const rewrittenBody = rewritten.html;

  const { mainContent, quotedContent, hasQuotedContent } = React.useMemo(
    () => splitEmailContentByBlockquote(rewrittenBody),
    [rewrittenBody],
  );

  if (!content) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading message content...
      </div>
    );
  }

  const baseClassName = 'block w-full min-w-0 overflow-x-auto [&_img]:max-w-full [&_img]:h-auto [&_table]:max-w-full [&_pre]:max-w-full [&_iframe]:max-w-full [&_iframe]:h-auto [&_blockquote]:border-l-4 [&_blockquote]:border-l-gray-300 [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:my-2';

  return (
    <div style={{ containerType: 'inline-size' }}>
      {/* Main content (before blockquotes) */}
      <div
        className={baseClassName}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(mainContent),
        }}
      />

      {/* Show trimmed content toggle button */}
      {hasQuotedContent && (
        <div className="my-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowQuotedContent(!showQuotedContent)}
            className="h-5 bg-muted/60 px-2 py-0 text-muted-foreground hover:bg-muted hover:text-foreground"
            title={showQuotedContent ? 'Hide expanded content' : 'Show trimmed content'}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </div>
      )}

      {/* Quoted content (blockquotes) - shown when toggled */}
      {hasQuotedContent && showQuotedContent && (
        <div
          className={baseClassName}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(quotedContent),
          }}
        />
      )}
    </div>
  );
}
